<template>
  <div class="mobile-header">
    <!-- Gradient Background Decoration -->
    <!-- <div class="header-decoration">
      <div class="decoration-circle circle-1"></div>
      <div class="decoration-circle circle-2"></div>
    </div> -->

    <div class="header-content">
      <div class="header-top-row">
        <Button
          v-if="backButton"
          fill="clear"
          color="primary"
          shape="round"
          @click="$emit('back-button-click')"
          class="back-button"
        >
          <IconRenderer icon="arrow-back-outline" size="medium" />
        </Button>
        <!-- Logo & Title Section -->
        <div class="title-section">
          <div class="logo-title-wrapper">
            <div v-if="logo" class="logo-container">
              <img :src="logo" :alt="title" class="header-logo" />
            </div>
            <div class="title-wrapper">
              <h1 class="page-title">
                <IonIcon v-if="icon" :icon="icon" class="title-icon" />
                <span class="title-text">{{ title }}</span>
              </h1>
              <p v-if="subtitle" class="page-subtitle">{{ subtitle }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Section -->
      <div v-if="$slots.actions" class="header-actions">
        <slot name="actions" />
      </div>
    </div>

    <!-- Secondary Content (breadcrumbs, filters, etc.) -->
    <div v-if="$slots.secondary" class="header-secondary">
      <slot name="secondary" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { IonIcon } from '@ionic/vue';
import Button from '../button/Button.vue';
import IconRenderer from '../icon/IconRenderer.vue';

interface Props {
  title: string;
  subtitle?: string;
  icon?: string;
  logo?: string;
  backButton?: boolean;
}

defineProps<Props>();
</script>

<style scoped>
.mobile-header {
  width: 100%;
  background: linear-gradient(
    135deg,
    var(--ion-background-color) 0%,
    var(--ion-background-color-primary) 100%
  );
  position: sticky;
  top: 0;
  z-index: 30;
  padding-bottom: 1rem;
  box-shadow: var(--ion-box-shadow-hover);
  overflow: hidden;
  /* border-radius: 0 0 24px 24px; */
}

.header-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.decoration-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(var(--ion-color-primary-rgb), 0.08);
}

.circle-1 {
  width: 150px;
  height: 150px;
  top: -70px;
  right: -30px;
}

.circle-2 {
  width: 100px;
  height: 100px;
  bottom: -40px;
  left: -20px;
}

.header-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1.25rem;
}

.header-top-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.title-section {
  text-align: center;
}

.logo-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.logo-container {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: rgba(var(--ion-color-primary-rgb), 1);
  backdrop-filter: blur(10px);
  border-radius: 14px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--ion-box-shadow-hover);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
}

.logo-container:hover {
  transform: scale(1.05);
  box-shadow: var(--ion-box-shadow-hover);
}

.header-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.title-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  min-width: 0;
  overflow: hidden;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--ion-text-color);
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  min-width: 0;
}

.title-icon {
  font-size: 1.4rem;
  color: rgba(0, 0, 0, 0.9);
}

.title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  color: var(--ion-color-primary);
}

.page-subtitle {
  margin: 4px 0 0 0;
  font-size: 0.85rem;
  color: var(--ion-text-color);
  font-weight: 400;
  line-height: 1.4;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0;
}

.header-actions :deep(h3) {
  color: var(--ion-text-color);
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0;
  text-align: center;
  line-height: 1.5;
}

.header-secondary {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding: 0.75rem 1.25rem 0;
  margin-top: 0.5rem;
}
</style>
